import React, { useCallback, useMemo, useEffect } from "react";
import { FormControlLabel, Switch, FormGroup, Paper, Typography, Tooltip, IconButton, Divider, Grid } from "@material-ui/core";
import { Info, Help, Warning, Hotel, DirectionsCar } from "@material-ui/icons";
import styled from "styled-components";

const StyledPaper = styled(Paper)`
  padding: 24px;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleText = styled.div`
  font-weight: 600;
  color: #1976d2;
  font-size: 1.25rem;
  line-height: 1.4;
`;

const FormGroupStyled = styled(FormGroup)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${props => props.theme && props.theme.palette ? props.theme.palette.divider : '#e0e0e0'};
  border-radius: 4px;
  background-color: ${props => props.theme && props.theme.palette ? props.theme.palette.background.paper : '#ffffff'};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.theme && props.theme.palette ? props.theme.palette.action.hover : '#f5f5f5'};
    border-color: ${props => props.theme && props.theme.palette ? props.theme.palette.primary.main : '#1976d2'};
  }
  
  ${props => props.disabled && `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const SwitchLabel = styled.div`
  flex: 1;
  margin-left: 8px;
`;

const Alert = styled.div`
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
  
  ${props => props.severity === 'warning' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.warning.light : '#fff3e0'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.warning.contrastText : '#ef6c00'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.warning.main : '#ff9800'};
  `}
  
  ${props => props.severity === 'info' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.info.light : '#e3f2fd'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.info.contrastText : '#1565c0'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.info.main : '#2196f3'};
  `}
`;

const DividerStyled = styled(Divider)`
  margin: 16px 0;
`;

/**
 * Componente optimizado para las opciones del tour
 */
const TourOptions = React.memo(({ 
  options, 
  onChange, 
  disabled = false, 
  modelName, 
  hasPassenger, 
  warnings = [], 
  info = [],
  tourData = null // Agregar tourData para acceder a los valores iniciales
}) => {

  // Memoizar las opciones disponibles para evitar re-renders innecesarios
  const availableOptions = useMemo(() => {
    const baseOptions = [
      {
        key: "gps",
        label: "GPS",
        description: "Dispositivo GPS para navegación",
        disabled: modelName === "K1600GT", // K1600GT incluye GPS por defecto
        icon: <Info fontSize="small" />
      },
      {
        key: "insurance",
        label: "Seguro VIP",
        description: "Seguro de viaje con cobertura extendida",
        disabled: false,
        icon: <Help fontSize="small" />
      },
      {
        key: "cancelInsurance",
        label: "Seguro de Cancelación",
        description: "Seguro para cancelación del tour",
        disabled: false,
        icon: <Warning fontSize="small" />
      },
      {
        key: "passenger",
        label: "Pasajero",
        description: "Incluir pasajero en el tour",
        disabled: !hasPassenger,
        icon: <Info fontSize="small" />
      },
      {
        key: "singleRoom",
        label: "Habitación Individual",
        description: "Habitación individual en lugar de compartida",
        disabled: !hasPassenger,
        icon: <Hotel fontSize="small" />
      }
    ];

    return baseOptions.filter(option => !option.disabled);
  }, [modelName, hasPassenger]);

  // Autoseleccionar opciones cuando ya hay valores en el state
  useEffect(() => {
    if (tourData && tourData.booking) {
      const newOptions = { ...options };
      let hasChanges = false;

      // Autoseleccionar GPS si ya está configurado
      if (tourData.booking.r_gps && !options.gps) {
        newOptions.gps = true;
        hasChanges = true;
      }

      // Autoseleccionar seguro VIP si ya está configurado
      if (tourData.booking.r_insurance && !options.insurance) {
        newOptions.insurance = true;
        hasChanges = true;
      }

      // Autoseleccionar seguro de cancelación si ya está configurado
      if (tourData.booking.r_cancel_insurance && !options.cancelInsurance) {
        newOptions.cancelInsurance = true;
        hasChanges = true;
      }

      // Autoseleccionar pasajero si ya está configurado
      if (tourData.booking.p_first_name && !options.passenger) {
        newOptions.passenger = true;
        hasChanges = true;
      }

      // Autoseleccionar habitación individual/doble si ya está configurado
      if (tourData.booking.r_single !== undefined) {
        if (tourData.booking.r_single && !options.single) {
          newOptions.single = true;
          newOptions.double = false;
          hasChanges = true;
        } else if (!tourData.booking.r_single && !options.double) {
          newOptions.single = false;
          newOptions.double = true;
          hasChanges = true;
        }
      }

      // Autoseleccionar asiento bajo si ya está configurado
      if (tourData.booking.low_seat && !options.lowSeat) {
        newOptions.lowSeat = true;
        hasChanges = true;
      }

      if (hasChanges) {
        onChange(newOptions);
      }
    }
  }, [tourData, options, onChange]);

  // Memoizar el manejador de cambios para evitar re-renders
  const handleOptionChange = useCallback((optionKey) => (event) => {
    const newOptions = {
      ...options,
      [optionKey]: event.target.checked
    };

    // Lógica especial para single/double (mutuamente excluyentes)
    if (optionKey === "single") {
      newOptions.double = !event.target.checked;
    } else if (optionKey === "double") {
      newOptions.single = !event.target.checked;
    }

    onChange(newOptions);
  }, [options, onChange]);

  // Filtrar advertencias e información relevantes
  const relevantWarnings = useMemo(() => {
    return warnings.filter(warning => 
      warning.toLowerCase().includes('gps') || 
      warning.toLowerCase().includes('seguro') ||
      warning.toLowerCase().includes('pasajero') ||
      warning.toLowerCase().includes('habitación')
    );
  }, [warnings]);

  const relevantInfo = useMemo(() => {
    return info.filter(infoItem => 
      infoItem.toLowerCase().includes('gps') || 
      infoItem.toLowerCase().includes('seguro') ||
      infoItem.toLowerCase().includes('pasajero') ||
      infoItem.toLowerCase().includes('habitación')
    );
  }, [info]);

  return (
    <StyledPaper>
      <TitleContainer>
        <TitleText>
          Opciones del Tour
        </TitleText>
        <Tooltip title="Configura las opciones adicionales para el tour">
          <IconButton size="small">
            <Help fontSize="small" />
          </IconButton>
        </Tooltip>
      </TitleContainer>

      <FormGroupStyled>
        {availableOptions.map((option) => (
          <SwitchContainer key={option.key} disabled={disabled || option.disabled}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {option.icon}
              <SwitchLabel>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.description}
                </Typography>
              </SwitchLabel>
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={options[option.key] || false}
                  onChange={handleOptionChange(option.key)}
                  disabled={disabled || option.disabled}
                  color="primary"
                />
              }
              label=""
            />
          </SwitchContainer>
        ))}
      </FormGroupStyled>

      {/* Opciones de motocicleta (Single/Double) */}
      <DividerStyled />
      <Typography variant="h6" style={{ marginBottom: 16, color: '#1976d2' }}>
        Tipo de Habitación
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <SwitchContainer disabled={disabled}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Hotel fontSize="small" />
              <SwitchLabel>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Habitación Individual
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Habitación individual para el conductor
                </Typography>
              </SwitchLabel>
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={options.single || false}
                  onChange={handleOptionChange("single")}
                  disabled={disabled}
                  color="primary"
                />
              }
              label=""
            />
          </SwitchContainer>
        </Grid>
        <Grid item xs={6}>
          <SwitchContainer disabled={disabled}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Hotel fontSize="small" />
              <SwitchLabel>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Habitación Doble
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Habitación compartida (por defecto)
                </Typography>
              </SwitchLabel>
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={options.double || false}
                  onChange={handleOptionChange("double")}
                  disabled={disabled}
                  color="primary"
                />
              }
              label=""
            />
          </SwitchContainer>
        </Grid>
      </Grid>

      {/* Opción de asiento bajo */}
      <DividerStyled />
      <Typography variant="h6" style={{ marginBottom: 16, color: '#1976d2' }}>
        Opciones de Vehículo
      </Typography>
      <SwitchContainer disabled={disabled}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <DirectionsCar fontSize="small" />
          <SwitchLabel>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              Asiento Bajo
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Solicitar asiento bajo para mayor comodidad
            </Typography>
          </SwitchLabel>
        </div>
        <FormControlLabel
          control={
            <Switch
              checked={options.lowSeat || false}
              onChange={handleOptionChange("lowSeat")}
              disabled={disabled}
              color="primary"
            />
          }
          label=""
        />
      </SwitchContainer>

      {/* Mostrar advertencias relevantes */}
      {relevantWarnings.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Advertencias:</strong>
          </Typography>
          {relevantWarnings.map((warning, index) => (
            <Typography key={index} variant="body2">
              • {warning}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Mostrar información relevante */}
      {relevantInfo.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Información:</strong>
          </Typography>
          {relevantInfo.map((infoItem, index) => (
            <Typography key={index} variant="body2">
              • {infoItem}
            </Typography>
          ))}
        </Alert>
      )}

      <DividerStyled />
    </StyledPaper>
  );
});

TourOptions.displayName = 'TourOptions';

export default TourOptions; 


// WEBPACK FOOTER //
// ./src/components/tour/TourProcessing/components/TourOptions.js